import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { EnneagramType, Question } from "@/types/vocational"

interface QuestionCardProps {
  question: Question
  currentAnswer?: EnneagramType
  onAnswer: (answer: EnneagramType) => void
  questionNumber: number
  totalQuestions: number
}

export function QuestionCard({
  question,
  currentAnswer,
  onAnswer,
  questionNumber,
  totalQuestions,
}: QuestionCardProps) {
  const isScoring = !!question.options && question.options.length === 9
  if (!isScoring) {
    return null
  }

  const colorIndex = Math.floor((questionNumber - 1) / 5) % 2
  const bgClass = colorIndex === 0 ? "bg-title-blue" : "bg-[#ED7723]"
  const textClass = "text-white"
  const progressIndex = Math.max(questionNumber - 1, 0)
  const rawPercentage = totalQuestions > 0 ? (progressIndex / totalQuestions) * 100 : 0
  const percentage = Math.min(Math.max(Math.round(rawPercentage), 0), 100)

  return (
    <Card
      className={cn(
        "w-full max-w-3xl mx-auto p-6 md:p-8 shadow-card border-0",
        isScoring ? bgClass : "bg-card",
        isScoring ? textClass : "text-foreground"
      )}
    >
      <div className="space-y-6">
        <div className="space-y-3">
          <div className={cn("flex justify-between items-center text-xs", isScoring ? "text-white/80" : "text-muted-foreground")}>
            <span>Questao {questionNumber} de {totalQuestions}</span>
            <span>{percentage}%</span>
          </div>
          <div className={cn("h-2 rounded-full", isScoring ? "bg-white/20" : "bg-muted")}>
            <div
              className={cn("h-full rounded-full", isScoring ? "bg-white" : "bg-primary")}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        <h2 className={cn("text-xl md:text-2xl font-semibold whitespace-pre-line", isScoring ? "text-white" : "text-foreground")}>
          {question.question}
        </h2>

        <div className="grid gap-2">
          {question.options?.map((option) => (
            <button
              key={`${question.id}-${option.value}`}
              type="button"
              onClick={() => onAnswer(option.value)}
              className={cn(
                "w-full text-left rounded-lg border p-3 transition-colors",
                isScoring
                  ? "border-white/30 text-white hover:bg-white/10"
                  : "border-border hover:bg-accent/40"
              )}
            >
              {option.text}
            </button>
          ))}
        </div>
      </div>
    </Card>
  )
}
